// ============================================
// FAQ 支持中心首页 — 紧凑工作台
// ============================================

import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowRight,
  BookOpen,
  CheckCircle2,
  Clock3,
  Eye,
  LifeBuoy,
  Loader2,
  MessageSquare,
  Plus,
  Search,
  Settings,
  UserCircle,
} from 'lucide-react';
import { useAuthStore } from '../stores/auth-store';
import { useUserStore } from '../stores/user-store';
import FaqAuthModal from './FaqAuthModal';
import FaqQuestionModal from './FaqQuestionModal';
import { useFaqStore } from './faq-store';
import { useFaqQuestionStore } from './faq-question-store';
import { tiptapToPlainText } from './faq-rich-content';
import { faqTagLabels } from './faq-tag-utils';
import { useFaqSpace } from './FaqSpaceContext';
import type { FaqQuestionStatus } from './types';

const statusConfig: Record<FaqQuestionStatus, { label: string; className: string }> = {
  open: { label: '待回复', className: 'bg-amber-50 text-amber-600 border-amber-100' },
  answered: { label: '已回复', className: 'bg-emerald-50 text-emerald-600 border-emerald-100' },
  closed: { label: '已关闭', className: 'bg-slate-100 text-slate-500 border-slate-200' },
};

function fmtTime(value: string | null) {
  if (!value) return '-';
  return new Date(value).toLocaleString('zh-CN', {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default function FaqSupportHomePage() {
  const navigate = useNavigate();
  const { space, path, adminPath } = useFaqSpace();
  const { user } = useAuthStore();
  const { profile } = useUserStore();
  const {
    items,
    loading: faqLoading,
    fetchItems,
    fetchTags,
    getPrimaryTags,
    getSecondaryTags,
    setSearchQuery,
  } = useFaqStore();
  const {
    questions,
    loading: questionLoading,
    fetchQuestions,
  } = useFaqQuestionStore();

  const [query, setQuery] = useState('');
  const [askOpen, setAskOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const allTags = [...getPrimaryTags(), ...getSecondaryTags()];

  useEffect(() => {
    fetchItems();
    fetchTags();
  }, [fetchItems, fetchTags]);

  useEffect(() => {
    if (user) fetchQuestions();
  }, [user, fetchQuestions]);

  const publishedFaqs = useMemo(
    () => items.filter((item) => item.is_published && !item.deleted_at),
    [items],
  );

  const latestFaqs = useMemo(() => (
    [...publishedFaqs]
      .sort((a, b) => new Date(b.updated_at || b.created_at).getTime() - new Date(a.updated_at || a.created_at).getTime())
      .slice(0, 5)
  ), [publishedFaqs]);

  const openQuestions = questions.filter((item) => item.status === 'open');
  const answeredQuestions = questions.filter((item) => item.status === 'answered');
  const myQuestions = user ? questions.filter((item) => item.created_by === user.id) : [];
  const recentQuestions = [...questions]
    .sort((a, b) => new Date(b.last_reply_at || b.updated_at || b.created_at).getTime() - new Date(a.last_reply_at || a.updated_at || a.created_at).getTime())
    .slice(0, 5);

  function handleSearchSubmit(e: FormEvent) {
    e.preventDefault();
    setSearchQuery(query.trim());
    navigate(path('knowledge'));
  }

  function handleAskClick() {
    if (!user) {
      setAuthOpen(true);
      return;
    }
    setAskOpen(true);
  }

  const canManageFaq = !!profile?.is_faq_admin || profile?.role === 'super_admin';
  const supportTarget = canManageFaq ? adminPath('questions') : user ? `${path('questions')}?scope=mine` : '#';
  const supportLabel = canManageFaq ? '处理留言' : '我的支持';
  const supportCount = canManageFaq ? openQuestions.length : myQuestions.length;

  return (
    <div className="h-full overflow-y-auto bg-slate-50">
      <div className="mx-auto max-w-7xl px-4 py-4 sm:px-6 lg:px-8">
        <section className="rounded-2xl border border-slate-200 bg-white px-4 py-4 shadow-sm">
          <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-sky-50 text-sky-600">
                  <LifeBuoy size={18} />
                </span>
                <div>
                  <h1 className="text-lg font-bold text-slate-800">{space.title}</h1>
                  <p className="mt-0.5 text-xs text-slate-400">查知识、看留言、追踪处理状态</p>
                </div>
              </div>
            </div>

            <form onSubmit={handleSearchSubmit} className="flex min-w-0 flex-1 gap-2 xl:max-w-2xl">
              <div className="relative min-w-0 flex-1">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="搜索 FAQ、系统问题、业务流程"
                  className="h-10 w-full rounded-xl border border-slate-200 bg-slate-50 pl-10 pr-3 text-sm outline-none transition focus:border-sky-300 focus:bg-white focus:ring-2 focus:ring-sky-100"
                />
              </div>
              <button
                type="submit"
                className="inline-flex h-10 items-center justify-center rounded-xl bg-sky-500 px-4 text-sm font-semibold text-white shadow-sm transition hover:bg-sky-600"
              >
                搜索
              </button>
            </form>

            <div className="flex shrink-0 flex-wrap items-center gap-2">
              <Link
                to={path('knowledge')}
                className="inline-flex h-10 items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-600 transition hover:border-sky-200 hover:bg-sky-50 hover:text-sky-600"
              >
                <BookOpen size={15} />
                知识问答
              </Link>
              <button
                onClick={handleAskClick}
                className="inline-flex h-10 items-center gap-1.5 rounded-xl bg-slate-900 px-3 text-sm font-semibold text-white transition hover:bg-slate-800"
              >
                <Plus size={15} />
                我要提问
              </button>
            </div>
          </div>
        </section>

        <div className="mt-4 grid gap-3 md:grid-cols-3">
          <Link
            to={path('knowledge')}
            className="group flex min-h-[108px] items-center justify-between rounded-2xl border border-slate-200 bg-white px-4 py-4 transition hover:border-sky-200 hover:shadow-sm"
          >
            <div className="min-w-0">
              <p className="text-sm font-bold text-slate-800">知识问答</p>
              <p className="mt-1 text-xs text-slate-400">{publishedFaqs.length} 条已发布 FAQ</p>
              <p className="mt-3 text-xs font-semibold text-sky-600">进入知识库</p>
            </div>
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-sky-50 text-sky-600">
              <BookOpen size={20} />
            </span>
          </Link>

          <Link
            to={path('questions')}
            className="group flex min-h-[108px] items-center justify-between rounded-2xl border border-slate-200 bg-white px-4 py-4 transition hover:border-sky-200 hover:shadow-sm"
          >
            <div className="min-w-0">
              <p className="text-sm font-bold text-slate-800">留言中心</p>
              <p className="mt-1 text-xs text-slate-400">{user ? `${openQuestions.length} 条待回复` : '登录后查看内部讨论'}</p>
              <p className="mt-3 text-xs font-semibold text-emerald-600">查看留言</p>
            </div>
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600">
              <MessageSquare size={20} />
            </span>
          </Link>

          <Link
            to={supportTarget}
            onClick={(e) => {
              if (!user) {
                e.preventDefault();
                setAuthOpen(true);
              }
            }}
            className="group flex min-h-[108px] items-center justify-between rounded-2xl border border-slate-200 bg-white px-4 py-4 transition hover:border-sky-200 hover:shadow-sm"
          >
            <div className="min-w-0">
              <p className="text-sm font-bold text-slate-800">{supportLabel}</p>
              <p className="mt-1 text-xs text-slate-400">{user ? `${supportCount} 条相关留言` : '需要登录'}</p>
              <p className="mt-3 text-xs font-semibold text-indigo-600">{canManageFaq ? '进入处理台' : '查看进度'}</p>
            </div>
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600">
              {canManageFaq ? <Settings size={20} /> : <UserCircle size={20} />}
            </span>
          </Link>
        </div>

        <div className="mt-4 grid items-stretch gap-4 xl:grid-cols-2">
          <section className="flex h-[430px] flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="flex h-14 shrink-0 items-center justify-between border-b border-slate-100 px-4">
              <div>
                <h2 className="text-sm font-bold text-slate-800">最新知识问答</h2>
                <p className="mt-0.5 text-[11px] text-slate-400">正式沉淀的答案</p>
              </div>
              <Link to={path('knowledge')} className="inline-flex items-center gap-1 text-xs font-semibold text-sky-600 hover:text-sky-700">
                全部
                <ArrowRight size={13} />
              </Link>
            </div>

            <div className="min-h-0 flex-1 divide-y divide-slate-100">
              {faqLoading && (
                <div className="flex h-full items-center justify-center">
                  <Loader2 size={22} className="animate-spin text-sky-500" />
                </div>
              )}

              {!faqLoading && latestFaqs.length === 0 && (
                <div className="flex h-full items-center justify-center text-sm text-slate-400">
                  暂无已发布 FAQ
                </div>
              )}

              {!faqLoading && latestFaqs.map((item) => (
                <Link
                  key={item.id}
                  to={path(item.id)}
                  className="flex min-h-[62px] items-center gap-3 px-4 py-3 transition hover:bg-sky-50/50"
                >
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-sky-50 text-xs font-bold text-sky-600">Q</span>
                  <div className="min-w-0 flex-1">
                    <h3 className="line-clamp-1 text-sm font-semibold text-slate-700">{item.question}</h3>
                    <div className="mt-1 flex items-center gap-1.5">
                      {faqTagLabels(item, allTags, 1).slice(0, 2).map((tag) => (
                        <span key={tag} className="rounded bg-sky-50 px-1.5 py-0.5 text-[10px] font-medium text-sky-600">{tag}</span>
                      ))}
                    </div>
                  </div>
                  <span className="flex shrink-0 items-center gap-1 text-[11px] text-slate-400">
                    <Eye size={12} />
                    {item.view_count}
                  </span>
                </Link>
              ))}
            </div>
          </section>

          <section className="flex h-[430px] flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="flex h-14 shrink-0 items-center justify-between border-b border-slate-100 px-4">
              <div>
                <h2 className="text-sm font-bold text-slate-800">留言动态</h2>
                <p className="mt-0.5 text-[11px] text-slate-400">员工问题和回复进度</p>
              </div>
              <Link to={path('questions')} className="inline-flex items-center gap-1 text-xs font-semibold text-sky-600 hover:text-sky-700">
                全部
                <ArrowRight size={13} />
              </Link>
            </div>

            <div className="min-h-0 flex-1 divide-y divide-slate-100">
              {!user && (
                <div className="flex h-full flex-col items-center justify-center px-4 text-center">
                  <MessageSquare size={34} className="mb-2 text-slate-300" />
                  <p className="text-sm font-semibold text-slate-600">登录后查看内部留言</p>
                  <button
                    onClick={() => setAuthOpen(true)}
                    className="mt-3 rounded-lg bg-sky-50 px-3 py-1.5 text-xs font-semibold text-sky-600 transition hover:bg-sky-100"
                  >
                    登录 / 注册
                  </button>
                </div>
              )}

              {user && questionLoading && (
                <div className="flex h-full items-center justify-center">
                  <Loader2 size={22} className="animate-spin text-sky-500" />
                </div>
              )}

              {user && !questionLoading && recentQuestions.length === 0 && (
                <div className="flex h-full items-center justify-center text-sm text-slate-400">
                  暂无留言
                </div>
              )}

              {user && !questionLoading && recentQuestions.map((item) => (
                <Link
                  key={item.id}
                  to={path(`questions/${item.id}`)}
                  className="flex min-h-[62px] items-center gap-3 px-4 py-3 transition hover:bg-sky-50/50"
                >
                  <span className={`shrink-0 rounded-lg border px-2 py-0.5 text-[11px] font-semibold ${statusConfig[item.status].className}`}>
                    {statusConfig[item.status].label}
                  </span>
                  <div className="min-w-0 flex-1">
                    <h3 className="line-clamp-1 text-sm font-semibold text-slate-700">{item.title}</h3>
                    <p className="mt-1 line-clamp-1 text-xs text-slate-400">{tiptapToPlainText(item.content)}</p>
                  </div>
                  <span className="shrink-0 text-[11px] text-slate-400">{fmtTime(item.last_reply_at || item.updated_at)}</span>
                </Link>
              ))}
            </div>
          </section>
        </div>

        <div className="mt-4 grid gap-3 sm:grid-cols-3">
          <div className="flex h-24 items-center justify-between rounded-2xl border border-slate-200 bg-white px-4">
            <div>
              <p className="text-xs font-semibold text-slate-500">已发布 FAQ</p>
              <p className="mt-2 text-2xl font-bold text-slate-800">{publishedFaqs.length}</p>
            </div>
            <BookOpen size={20} className="text-slate-300" />
          </div>
          <div className="flex h-24 items-center justify-between rounded-2xl border border-slate-200 bg-white px-4">
            <div>
              <p className="text-xs font-semibold text-slate-500">待回复留言</p>
              <p className="mt-2 text-2xl font-bold text-slate-800">{user ? openQuestions.length : '-'}</p>
            </div>
            <Clock3 size={20} className="text-slate-300" />
          </div>
          <div className="flex h-24 items-center justify-between rounded-2xl border border-slate-200 bg-white px-4">
            <div>
              <p className="text-xs font-semibold text-slate-500">已回复留言</p>
              <p className="mt-2 text-2xl font-bold text-slate-800">{user ? answeredQuestions.length : '-'}</p>
            </div>
            <CheckCircle2 size={20} className="text-slate-300" />
          </div>
        </div>
      </div>

      <FaqQuestionModal isOpen={askOpen} onClose={() => setAskOpen(false)} />
      <FaqAuthModal isOpen={authOpen} onClose={() => setAuthOpen(false)} />
    </div>
  );
}
